/**
 * Soft inbound-message chime via Web Audio API (no audio assets).
 * Call {@link NotificationSound.unlock} from a user gesture when possible.
 */
export class NotificationSound {
  private ctx: AudioContext | null = null;
  private lastPlayedAt = 0;
  private readonly minIntervalMs: number;
  private readonly volume: number;

  constructor(options?: { minIntervalMs?: number; volume?: number }) {
    this.minIntervalMs = options?.minIntervalMs ?? 700;
    this.volume = Math.min(1, Math.max(0, options?.volume ?? 0.22));
  }

  unlock(): void {
    const ctx = this.ensureContext();
    if (ctx.state === 'suspended') {
      void ctx.resume().catch(() => undefined);
    }
  }

  async play(): Promise<void> {
    const now = Date.now();
    if (now - this.lastPlayedAt < this.minIntervalMs) {
      return;
    }
    this.lastPlayedAt = now;

    try {
      const ctx = this.ensureContext();
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      this.chime(ctx);
    } catch {
      // Autoplay blocked — ignore.
    }
  }

  private ensureContext(): AudioContext {
    if (!this.ctx) {
      const Ctx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctx) {
        throw new Error('Web Audio API unavailable');
      }
      this.ctx = new Ctx();
    }
    return this.ctx;
  }

  private chime(ctx: AudioContext): void {
    const t0 = ctx.currentTime;
    this.tone(ctx, 1046.5, t0, 0.12);
    this.tone(ctx, 1318.5, t0 + 0.1, 0.16);
  }

  private tone(
    ctx: AudioContext,
    frequency: number,
    start: number,
    duration: number,
  ): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(frequency, start);

    const peak = this.volume;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(peak, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + duration + 0.02);
  }
}

export const messageNotificationSound = new NotificationSound();

export function bindNotificationSoundUnlock(
  target: Document | HTMLElement = document,
): () => void {
  const unlock = () => messageNotificationSound.unlock();
  const opts = { capture: true, passive: true } as const;
  target.addEventListener('pointerdown', unlock, opts);
  target.addEventListener('keydown', unlock, opts);
  return () => {
    target.removeEventListener('pointerdown', unlock, opts);
    target.removeEventListener('keydown', unlock, opts);
  };
}
